// Composites the lit scene with the sky, aerial perspective, local fog, volumetric sun shafts and clouds.

export const compositeFrag = /* glsl */ `
uniform sampler2D uSceneColor;
uniform sampler2D uSceneDepth;
uniform sampler2D uTransLUT;
uniform sampler2D uMSLUT;
uniform sampler2D uClouds;
uniform sampler2D uMilkyWay;
uniform sampler2D uSkyIrr;
uniform mat4 uInvProjView;
uniform vec3 uCamPos;
uniform vec3 uSunDirW;
uniform vec3 uSunE;
uniform vec3 uMoonDirW;
uniform vec3 uMoonE;
uniform float uMoonIllum;
uniform vec3 uMoonLitDir;   // direction from moon toward sun (for phase shading)
uniform vec4 uFog;          // x density (1/m) at base, y base altitude, z scale height (m), w valley fog density (1/m)
uniform vec4 uValley;       // x top altitude, y thickness, z noise amount, w time
uniform vec3 uFogAlbedo;
uniform float uPrecipHaze;  // extra extinction near camera (rain/snow)
uniform float uLST;
uniform float uLat;
uniform float uStarVis;
uniform vec2 uResolution;
uniform float uCloudsOn;
uniform float uLightning;
uniform vec3 uLightningPos;
uniform float uCloudFar;
uniform float uCloudCover;
in vec2 vUv;
out vec4 fragColor;

vec3 worldFromDepth(vec2 uv, float d) {
  vec4 p = uInvProjView * vec4(uv * 2.0 - 1.0, d * 2.0 - 1.0, 1.0);
  return p.xyz / p.w;
}

float hgPhase(float g, float c) {
  float g2 = g * g;
  return (1.0 - g2) / (4.0 * PI * pow(max(1.0 + g2 - 2.0 * g * c, 1e-4), 1.5));
}

float fogDensity(vec3 p) {
  return uFog.x * exp(-max(p.y - uFog.y, 0.0) / max(uFog.z, 1.0));
}
// valley / inversion fog filling the amphitheater, with a soft billowy top
float valleyDensity(vec3 p) {
  float n = vnoise3(vec3(p.xz * 0.006, uValley.w * 0.02)) * 0.6 + vnoise3(vec3(p.xz * 0.023, uValley.w * 0.05 + 3.0)) * 0.4;
  float top = uValley.x + (n - 0.5) * uValley.z;
  return uFog.w * smoothstep(top, top - uValley.y, p.y);
}

void valleyFog(vec3 ro, vec3 rd, float tMax, out vec3 ins, out float trans) {
  ins = vec3(0.0);
  trans = 1.0;
  if (uFog.w <= 0.0) return;
  float topAlt = uValley.x + uValley.z * 0.6;
  // interval of the ray below the fog top
  float t0 = 0.0, t1 = tMax;
  if (abs(rd.y) > 1e-4) {
    float tc = (topAlt - ro.y) / rd.y;
    if (ro.y > topAlt) { if (rd.y >= 0.0) return; t0 = max(tc, 0.0); }
    else if (rd.y > 0.0) t1 = min(t1, tc);
  } else if (ro.y > topAlt) return;
  t1 = min(t1, 30000.0);
  if (t1 <= t0) return;
  float cosS = dot(rd, uSunDirW);
  float ph = hgPhase(0.5, cosS);
  vec3 camMM = vec3(0.0, uGroundRadiusMM + max(uValley.x, 0.0) * 1e-6, 0.0);
  vec3 sunCol = lutTransmittance(uTransLUT, camMM, uSunDirW) * uSunE * (1.0 - 0.8 * uCloudsOn);
  vec3 amb = texelFetch(uSkyIrr, ivec2(0, 0), 0).rgb * 0.282095 * PI * 1.15;
  const int N = 24;
  float dt = (t1 - t0) / float(N);
  float jitter = hash12(gl_FragCoord.xy);
  for (int i = 0; i < N; i++) {
    float t = t0 + (float(i) + jitter) * dt;
    vec3 p = ro + rd * t;
    float d = valleyDensity(p);
    if (d <= 0.0) continue;
    // fog top is sunlit, deeper fog is in its own shade
    float depthIn = max(0.0, uValley.x - p.y);
    float selfShadow = exp(-d * depthIn * 1.5 / max(uSunDirW.y, 0.08));
    vec3 S = d * uFogAlbedo * (sunCol * ph * selfShadow + amb);
    float st = exp(-d * dt);
    ins += trans * (S - S * st) / d;
    trans *= st;
    if (trans < 0.01) break;
  }
}

// Integrate atmosphere + fog between the camera and a point at distance tMax (meters) along rd.
void aerial(vec3 ro, vec3 rd, float tMax, bool isSky, out vec3 inscatter, out vec3 trans) {
  inscatter = vec3(0.0);
  trans = vec3(1.0);
  const int N = 20;
  float cosS = dot(rd, uSunDirW);
  float cosM = dot(rd, uMoonDirW);
  float rpS = rayleighPhase(-cosS), mpS = miePhase(cosS);
  float rpM = rayleighPhase(-cosM), mpM = miePhase(cosM);
  float fogPS = hgPhase(0.55, cosS), fogPM = hgPhase(0.55, cosM);
  vec3 ambient = texelFetch(uSkyIrr, ivec2(0, 0), 0).rgb * 0.282095 * PI * 1.1; // isotropic sky irradiance/PI
  float jitter = hash12(gl_FragCoord.xy + fract(uValley.w) * 91.0);
  float tPrev = 0.0;
  for (int i = 0; i < N; i++) {
    float f = (float(i) + jitter) / float(N);
    float t = tMax * f * f;
    float dt = t - tPrev;
    tPrev = t;
    vec3 p = ro + rd * t;
    // atmosphere (MM units)
    vec2 dxz = p.xz - ro.xz;
    float altTrue = p.y + dot(dxz, dxz) / (2.0 * 6371000.0);
    vec3 pMM = vec3(0.0, uGroundRadiusMM + max(altTrue, 0.0) * 1e-6, 0.0);
    vec3 rs, ext; float ms;
    scatteringValues(pMM, rs, ms, ext);
    vec3 tS = lutTransmittance(uTransLUT, pMM, uSunDirW);
    vec3 msS = lutMultiScatter(uMSLUT, pMM, uSunDirW);
    vec3 S = (rs * (rpS * tS + msS) + ms * (mpS * tS + msS)) * uSunE * mix(1.0, uCloudFar, uCloudsOn);
    if (uMoonE.g > 0.0) {
      vec3 tM = lutTransmittance(uTransLUT, pMM, uMoonDirW);
      vec3 msM = lutMultiScatter(uMSLUT, pMM, uMoonDirW);
      S += (rs * (rpM * tM + msM) + ms * (mpM * tM + msM)) * uMoonE;
    }
    // to per-meter
    S *= 1e-6;
    vec3 E = ext * 1e-6;
    if (isSky) { S = vec3(0.0); E = vec3(0.0); } // sky LUT already has the atmosphere
    float fd = fogDensity(p) + uPrecipHaze * exp(-t / 400.0);
    vec3 fogS = fd * uFogAlbedo * (tS * uSunE * fogPS * (1.0 - 0.8 * uCloudsOn) + ambient + uLightning * vec3(0.06));
    if (uMoonE.g > 0.0) fogS += fd * uFogAlbedo * lutTransmittance(uTransLUT, pMM, uMoonDirW) * uMoonE * fogPM;
    S += fogS;
    E += vec3(fd);
    vec3 st = exp(-E * dt);
    inscatter += trans * (S - S * st) / max(E, vec3(1e-9));
    trans *= st;
  }
}

// --- celestial bodies
vec2 radec(vec3 d) {
  float E = d.x, U = d.y, Nn = -d.z;
  float sl = sin(uLat), cl = cos(uLat);
  float sinDec = sl * U + cl * Nn;
  float H = atan(-E, cl * U - sl * Nn);
  float ra = uLST - H;
  return vec2(ra, asin(clamp(sinDec, -1.0, 1.0)));
}

vec3 starField(vec3 d, float pixAng) {
  vec2 rd = radec(d);
  // procedural stars on an equal-area grid in (ra, sin(dec))
  vec2 g = vec2(rd.x / (2.0 * PI) * 1600.0, (sin(rd.y) * 0.5 + 0.5) * 520.0);
  vec3 acc = vec3(0.0);
  vec2 cell = floor(g);
  for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
    vec2 c = cell + vec2(i, j);
    c.x = mod(c.x, 1600.0);
    float h = hash12(c * 1.37 + 0.11);
    if (h > 0.0075) continue; // ~6k stars over the whole sphere
    vec2 o = hash22(c * 3.1 + 7.7);
    vec2 sp = c + o;
    vec2 dd = g - sp;
    dd.x = dd.x - 1600.0 * floor(dd.x / 1600.0 + 0.5);
    // to radians approx
    float cosd = cos(rd.y);
    vec2 da = vec2(dd.x / 1600.0 * 2.0 * PI * cosd, dd.y / 520.0 * 2.0 / max(cosd, 0.05));
    float r2 = dot(da, da) / (pixAng * pixAng);
    // magnitude distribution: many faint, few bright
    // cumulative star counts grow ~x3 per magnitude: invert that distribution
    float u = hash12(c * 5.3 + 1.9);
    float m = 6.8 + log(max(u, 1e-4)) / log(3.0);
    if (m < -1.4) m = -1.4;
    float flux = pow(10.0, -0.4 * m) * 0.0034;
    float temp = hash12(c * 9.1 + 4.2);
    vec3 tint = temp < 0.25 ? vec3(1.0, 0.78, 0.6) : (temp < 0.55 ? vec3(1.0, 0.94, 0.86) : (temp < 0.85 ? vec3(0.9, 0.95, 1.0) : vec3(0.75, 0.85, 1.0)));
    float tw = 0.75 + 0.25 * sin(uValley.w * (3.0 + 7.0 * u) + u * 40.0);
    acc += tint * flux * tw * exp(-r2 * 1.4) / (pixAng * pixAng * 1e4);
  }
  return acc;
}

vec3 milkyWay(vec3 d) {
  vec2 rd = radec(d);
  vec2 uv = vec2(fract(rd.x / (2.0 * PI)), rd.y / PI + 0.5);
  return texture(uMilkyWay, uv).rgb * 0.0019;
}

vec3 moonDisk(vec3 rd) {
  float c = dot(rd, uMoonDirW);
  float r = 0.00454; // angular radius (rad)
  float ang = acos(clamp(c, -1.0, 1.0));
  if (ang > r * 1.6) return vec3(0.0);
  // local disk coordinates
  vec3 up = abs(uMoonDirW.y) < 0.99 ? vec3(0, 1, 0) : vec3(1, 0, 0);
  vec3 xa = normalize(cross(up, uMoonDirW));
  vec3 ya = cross(uMoonDirW, xa);
  vec2 q = vec2(dot(rd, xa), dot(rd, ya)) / r;
  float rr = dot(q, q);
  float disk = smoothstep(1.0, 0.92, sqrt(rr));
  vec3 nrm = normalize(xa * q.x + ya * q.y - uMoonDirW * sqrt(max(1.0 - rr, 0.0)));
  float lit = max(dot(nrm, uMoonLitDir), 0.0);
  float maria = 0.75 + 0.25 * fbm2(q * 2.3 + 5.0, 4);
  vec3 moonCol = vec3(1.0, 0.97, 0.92) * maria;
  return moonCol * (lit * 1.0 + 0.004) * disk * 6.0;
}

vec3 sunDisk(vec3 rd, vec3 trans) {
  float c = dot(rd, uSunDirW);
  float r = 0.00465;
  float ang = acos(clamp(c, -1.0, 1.0));
  float x = ang / r;
  if (x > 1.0) return vec3(0.0);
  float mu = sqrt(max(1.0 - x * x, 0.0));
  vec3 limb = pow(vec3(mu), vec3(0.42, 0.52, 0.66)); // limb darkening
  return limb * trans * 2600.0 * smoothstep(1.0, 0.97, x);
}

void main() {
  float d = texture(uSceneDepth, vUv).r;
  vec3 ro = uCamPos;
  vec3 far = worldFromDepth(vUv, 1.0);
  vec3 rd = normalize(far - ro);
  vec4 clouds = texture(uClouds, vUv);
  if (uCloudsOn < 0.5) clouds = vec4(0.0, 0.0, 0.0, 1.0);
  vec3 col;
  if (d >= 1.0) {
    vec3 camMM = vec3(0.0, uGroundRadiusMM + max(ro.y, 1.0) * 1e-6, 0.0);
    col = sampleSkyView(rd);
    float hz = rayIntersectSphere(camMM, rd, uGroundRadiusMM);
    vec3 tr = hz > 0.0 ? vec3(0.0) : lutTransmittance(uTransLUT, camMM, rd);
    float pixAng = 2.0 / uResolution.y * 0.62;
    vec3 night = (starField(rd, pixAng) + milkyWay(rd)) * uStarVis;
    col += (sunDisk(rd, vec3(1.0)) * uSunE.r / 3.14159 + moonDisk(rd) * uMoonE.g * 1.0e4 + night) * tr;
    // under a cloud deck the hazy band below the horizon is not sunlit
    float below = smoothstep(0.035, -0.02, rd.y) * uCloudsOn * smoothstep(0.5, 0.95, uCloudCover);
    col = mix(col, vec3(lum3(col)) * mix(1.0, uCloudFar, 0.8) * vec3(0.95, 0.97, 1.0), below);
    // local fog toward the horizon
    vec3 ins, tra, vins; float vtr;
    aerial(ro, rd, 25000.0, true, ins, tra);
    valleyFog(ro, rd, 30000.0, vins, vtr);
    col = col * clouds.a + clouds.rgb;
    col = (col * vtr + vins) * tra + ins;
  } else {
    vec3 wp = worldFromDepth(vUv, d);
    float dist = length(wp - ro);
    vec3 scene = texture(uSceneColor, vUv).rgb;
    vec3 ins, tra, vins; float vtr;
    aerial(ro, rd, dist, false, ins, tra);
    valleyFog(ro, rd, dist, vins, vtr);
    col = (scene * vtr + vins) * tra + ins;
    col = col * clouds.a + clouds.rgb;
  }
  fragColor = vec4(max(col, vec3(0.0)), 1.0);
}
`;
